import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { MessageCircle, X, Send } from 'lucide-react';
import { logChatbotQuestion } from '../../../../hooks/api/admin/chatbot';
import { getSessionData } from '../../../../hooks/api/auth/tokenManager';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChatbotData {
  totalBronzeQ: number;
  totalEligibleQ: number;
  bronzeAchieved: boolean;
  silverAchieved: boolean;
  goldAchieved: boolean;
  currentTier: 'gold' | 'silver' | 'bronze' | null;
  quarter: string;
  eligibleProcessesCount: number;
  bronze5ProcessesCount: number;
  allBronzeEligibleCount: number;
  notEligible5Count: number;
  notEligible10Count: number;
  totalQuarterSessions: number;
  isNueva: boolean;
}

interface Message {
  id: number;
  role: 'user' | 'assistant';
  text: string;
}

// ─── Knowledge base ───────────────────────────────────────────────────────────

interface KnowledgeEntry {
  triggers: string[];
  answer: (d: ChatbotData) => string;
}

const KNOWLEDGE: KnowledgeEntry[] = [
  {
    triggers: ['hola', 'buenas', 'hey', 'que tal', 'qué tal', 'como estas', 'cómo estás'],
    answer: (d) => {
      if (d.isNueva) return '¡Hola! Soy tu asistente del Programa Psi+ 2.0. Todavía estás en tu período inicial, así que el programa se activa cuando completes esa etapa. Mientras tanto puedo contarte cómo funciona para que ya sepas qué esperar.';
      if (d.currentTier === 'gold') return '¡Hola! Estás teniendo un trimestre increíble — ya desbloqueaste el bono de $2.000. ¿En qué te puedo ayudar?';
      if (d.currentTier === 'silver') return '¡Hola! Este trimestre ya alcanzaste el bono de $1.000. ¿Querés saber cómo llegar al de $2.000 o algo más del programa?';
      if (d.currentTier === 'bronze') return '¡Hola! Ya alcanzaste el bono de $500 este trimestre. ¿Querés ver qué falta para el siguiente nivel?';
      return '¡Hola! Soy tu asistente del Programa Psi+ 2.0. Puedo explicarte cómo funciona el programa, qué son las sesiones elegibles, cuánto te falta para un bono, o lo que necesites. ¿Por dónde empezamos?';
    },
  },
  {
    triggers: ['qué es', 'que es', 'cómo funciona', 'como funciona', 'explicame', 'explicá', 'programa', 'psi+', 'psi plus', 'resumen', 'descripción', 'descripcion'],
    answer: () =>
      'El Programa Psi+ 2.0 es un sistema de bonificaciones trimestrales que premia la retención de tus pacientes.\n\n' +
      'Hay 3 bonos posibles — y solo cobrás el más alto que alcances cada trimestre:\n\n' +
      '🥉 $500 — 150 sesiones elegibles de procesos con 5+ sesiones acumuladas\n' +
      '🥈 $1.000 — 150 sesiones elegibles de procesos con 10+ sesiones acumuladas\n' +
      '🥇 $2.000 — 250 sesiones elegibles de procesos con 10+ sesiones acumuladas\n\n' +
      'Lo clave: el período es trimestral, así que tenés 3 meses para acumular sesiones y podés ajustar tu estrategia antes de que cierre el período.',
  },
  {
    triggers: ['500', 'quinientos', 'bronce', 'bronze', 'primer bono', 'bono chico', 'bono más bajo', 'más fácil'],
    answer: (d) => {
      const falta = Math.max(0, 150 - d.totalBronzeQ);
      if (d.currentTier === 'silver' || d.currentTier === 'gold') return `El bono de $500 ya no aplica porque alcanzaste uno mejor. Solo se cobra el bono más alto del trimestre.`;
      if (d.bronzeAchieved) return `¡Ya alcanzaste el bono de $500! Llevás ${d.totalBronzeQ} sesiones elegibles del universo de 5+. Ahora el foco sería llegar a 150 del universo de 10+ para saltar al $1.000.`;
      return (
        `El bono de $500 se alcanza con 150 sesiones elegibles del universo de procesos con 5 o más sesiones acumuladas.\n\n` +
        `Una sesión es "elegible" para este bono a partir de la 5ª sesión de cada proceso.\n\n` +
        `Este trimestre llevás ${d.totalBronzeQ} sesiones elegibles${falta > 0 ? ` — te faltan ${falta} para alcanzarlo` : ` — ¡meta superada!`}.\n\n` +
        `Tenés ${d.allBronzeEligibleCount} proceso${d.allBronzeEligibleCount !== 1 ? 's' : ''} con sesiones elegibles para este bono.`
      );
    },
  },
  {
    triggers: ['1000', '1.000', 'mil', 'plata', 'silver', 'segundo bono'],
    answer: (d) => {
      const falta = Math.max(0, 150 - d.totalEligibleQ);
      if (d.currentTier === 'gold') return `El bono de $1.000 ya no aplica porque alcanzaste el de $2.000. Solo se cobra el más alto.`;
      if (d.silverAchieved) return `¡Ya alcanzaste el bono de $1.000! Llevás ${d.totalEligibleQ} sesiones elegibles del universo de 10+. Para el $2.000 necesitás 250 — te faltan ${Math.max(0, 250 - d.totalEligibleQ)}.`;
      return (
        `El bono de $1.000 requiere 150 sesiones elegibles de procesos con 10 o más sesiones acumuladas.\n\n` +
        `Para este universo, una sesión cuenta como elegible a partir de la 10ª sesión del proceso.\n\n` +
        `Este trimestre llevás ${d.totalEligibleQ} sesiones elegibles de ese universo${falta > 0 ? ` — te faltan ${falta}` : ` — ¡meta superada!`}.\n\n` +
        `Tenés ${d.eligibleProcessesCount} proceso${d.eligibleProcessesCount !== 1 ? 's' : ''} aportando sesiones elegibles.`
      );
    },
  },
  {
    triggers: ['2000', '2.000', 'dos mil', 'oro', 'gold', 'tercer bono', 'bono más alto', 'máximo'],
    answer: (d) => {
      const falta = Math.max(0, 250 - d.totalEligibleQ);
      if (d.goldAchieved) return `¡Ya alcanzaste el bono máximo de $2.000! Llevás ${d.totalEligibleQ} sesiones elegibles del universo de 10+.`;
      return (
        `El bono de $2.000 es el más alto — requiere 250 sesiones elegibles de procesos con 10 o más sesiones acumuladas.\n\n` +
        `Es el mismo universo que el $1.000 (10+ sesiones acumuladas), solo que la meta es más alta.\n\n` +
        `Este trimestre llevás ${d.totalEligibleQ}${falta > 0 ? ` — te faltan ${falta} para alcanzarlo` : ` — ¡ya lo tenés!`}.`
      );
    },
  },
  {
    triggers: ['sesión elegible', 'sesiones elegibles', 'qué cuenta', 'que cuenta', 'cuáles cuentan', 'cuales cuentan', 'cómo se cuenta', 'como se cuenta'],
    answer: () =>
      'Una sesión es "elegible" cuando pertenece a un proceso que ya cruzó su umbral de sesiones acumuladas:\n\n' +
      '• Para el bono de $500: a partir de la sesión 5 de cada proceso con 5+ sesiones. La 5ª, 6ª, 7ª... todas cuentan.\n' +
      '• Para los bonos de $1.000 y $2.000: a partir de la sesión 10 de procesos con 10+ sesiones. La 10ª, 11ª, 12ª... todas cuentan.\n\n' +
      'Solo cuentan las sesiones elegibles que ocurrieron DENTRO del trimestre actual. Las de trimestres anteriores no acumulan.',
  },
  {
    triggers: ['umbral', '5 sesiones', 'cinco sesiones', 'por qué la 5', 'desde cuándo', 'desde cuando', 'cuándo empieza', 'cuando empieza'],
    answer: () =>
      'Los umbrales marcan a partir de cuándo un proceso empieza a "madurar":\n\n' +
      '• Umbral de 5 sesiones → desde la sesión 5, el proceso aporta al bono de $500.\n' +
      '• Umbral de 10 sesiones → desde la sesión 10, el proceso aporta también a los bonos de $1.000 y $2.000.\n\n' +
      'La idea es premiar procesos terapéuticos que muestran adherencia real. Un paciente que lleva 5+ sesiones ya tiene un vínculo establecido.',
  },
  {
    triggers: ['10 sesiones', 'diez sesiones', 'por qué la 10', 'universo 10'],
    answer: () =>
      'El universo de 10+ sesiones es el universo de procesos más maduros — pacientes con más de 9 sesiones acumuladas contigo.\n\n' +
      'A partir de la sesión 10 de esos procesos, cada sesión adicional suma como elegible para los bonos de $1.000 y $2.000.\n\n' +
      'Un proceso de 10+ sesiones también contribuye al bono de $500 (a partir de la sesión 5, que ya la cruzó hace rato), así que esos procesos aportan a los tres bonos a la vez.',
  },
  {
    triggers: ['universo', 'universos', 'diferencia', 'cuál es la diferencia', 'cual es la diferencia', '5+ y 10+', '5+ vs 10+'],
    answer: () =>
      'Hay dos universos de sesiones elegibles:\n\n' +
      '🥉 Universo 5+ → procesos con 5 o más sesiones acumuladas. Bono: $500.\n' +
      '🥈🥇 Universo 10+ → procesos con 10 o más sesiones acumuladas. Bonos: $1.000 y $2.000.\n\n' +
      'Un proceso de 10+ sesiones pertenece a ambos universos — aporta tanto al $500 como al $1.000/$2.000.\n\n' +
      'Por eso la mejor estrategia es mantener activos tus procesos más largos: aportan el doble.',
  },
  {
    triggers: ['mi progreso', 'cómo voy', 'como voy', 'cuánto llevo', 'cuanto llevo', 'mi situación', 'mi situacion', 'mi estado', 'qué tengo', 'que tengo'],
    answer: (d) => {
      if (d.isNueva) return 'Todavía estás en tu período inicial. El programa se activa cuando lo completes.';
      const lines: string[] = [`Tu progreso en ${d.quarter}:\n`];
      if (d.currentTier === 'gold') lines.push(`🥇 Bono de $2.000 alcanzado — ${d.totalEligibleQ} sesiones elegibles (universo 10+).`);
      else if (d.currentTier === 'silver') lines.push(`🥈 Bono de $1.000 alcanzado — ${d.totalEligibleQ} / 150 sesiones elegibles (universo 10+). Te faltan ${250 - d.totalEligibleQ} para el de $2.000.`);
      else if (d.currentTier === 'bronze') lines.push(`🥉 Bono de $500 alcanzado — ${d.totalBronzeQ} / 150 sesiones elegibles (universo 5+). Para el $1.000 necesitás 150 del universo 10+ — llevás ${d.totalEligibleQ}.`);
      else {
        lines.push(`Sin bono alcanzado todavía.`);
        lines.push(`Universo 5+ (→$500): ${d.totalBronzeQ} / 150 sesiones elegibles.`);
        lines.push(`Universo 10+ (→$1.000/$2.000): ${d.totalEligibleQ} sesiones elegibles.`);
      }
      lines.push(`\nProcesos con sesiones elegibles: ${d.allBronzeEligibleCount} para $500, ${d.eligibleProcessesCount} para $1.000/$2.000.`);
      if (d.notEligible5Count > 0) lines.push(`Procesos cerca del umbral de 5: ${d.notEligible5Count}.`);
      if (d.notEligible10Count > 0) lines.push(`Procesos cerca del umbral de 10: ${d.notEligible10Count}.`);
      return lines.join('\n');
    },
  },
  {
    triggers: ['cuánto me falta', 'cuanto me falta', 'qué me falta', 'que me falta', 'faltan', 'faltante', 'necesito'],
    answer: (d) => {
      if (d.isNueva) return 'El programa todavía no aplica para vos — estás en el período inicial.';
      if (d.goldAchieved) return `¡Ya alcanzaste el bono máximo de $2.000! No te falta nada más este trimestre.`;
      const parts: string[] = [];
      if (!d.bronzeAchieved) parts.push(`Para el $500: te faltan ${150 - d.totalBronzeQ} sesiones elegibles del universo de 5+.`);
      if (!d.silverAchieved) parts.push(`Para el $1.000: te faltan ${150 - d.totalEligibleQ} sesiones elegibles del universo de 10+.`);
      else if (!d.goldAchieved) parts.push(`Para el $2.000: te faltan ${250 - d.totalEligibleQ} sesiones elegibles del universo de 10+.`);
      return parts.join('\n\n') || '¡Todos los bonos alcanzados!';
    },
  },
  {
    triggers: ['trimestre', 'período', 'periodo', 'cuándo cierra', 'cuando cierra', 'cuándo se cobra', 'cuando se cobra', 'mensual', 'anual'],
    answer: (d) =>
      `El programa funciona por trimestres. El período actual es ${d.quarter}.\n\n` +
      `Las sesiones elegibles se cuentan dentro del trimestre — lo que ocurra en Q1 no acumula para Q2, y viceversa.\n\n` +
      `La ventaja del esquema trimestral es que tenés 3 meses para ajustar tu estrategia si ves que vas por debajo del objetivo.`,
  },
  {
    triggers: ['proceso terapéutico', 'proceso terapeutico', 'qué es un proceso', 'que es un proceso', 'proceso activo'],
    answer: () =>
      'Un proceso terapéutico es la relación entre vos y un paciente bajo un tipo de terapia específico (individual, pareja, familia, etc.).\n\n' +
      'Cada proceso acumula sesiones a lo largo del tiempo. Cuando un proceso llega a su umbral (5 o 10 sesiones), empieza a aportar sesiones elegibles al trimestre en curso.\n\n' +
      'Un proceso pasa a inactivo si transcurren 30 días sin sesiones.',
  },
  {
    triggers: ['no elegible', 'no clasifica', 'no cuenta', 'por qué no cuenta', 'por que no cuenta', 'no aplica', 'sesiones que no cuentan'],
    answer: (d) => {
      const parts: string[] = ['Una sesión no cuenta como elegible si el proceso todavía no cruzó su umbral:'];
      parts.push(`• Para $500: el proceso necesita al menos 5 sesiones acumuladas. Tenés ${d.notEligible5Count} proceso${d.notEligible5Count !== 1 ? 's' : ''} que todavía no llegaron a ese umbral pero tuvieron sesiones este trimestre.`);
      parts.push(`• Para $1.000/$2.000: el proceso necesita al menos 10 sesiones acumuladas. Tenés ${d.notEligible10Count} proceso${d.notEligible10Count !== 1 ? 's' : ''} en esa situación.`);
      parts.push(`\nTambién: las sesiones elegibles de trimestres anteriores no se arrastran — solo cuentan las del período actual.`);
      return parts.join('\n\n');
    },
  },
  {
    triggers: ['mejorar', 'estrategia', 'cómo subo', 'como subo', 'qué hago', 'que hago', 'cómo llego', 'como llego', 'cómo puedo', 'como puedo', 'consejos', 'recomendación', 'recomendacion'],
    answer: (d) => {
      const tips: string[] = ['Para maximizar tus sesiones elegibles este trimestre:'];
      if (d.notEligible5Count > 0) tips.push(`• Tenés ${d.notEligible5Count} proceso${d.notEligible5Count !== 1 ? 's' : ''} cerca del umbral de 5 sesiones — si los llevás a la 5ª, empiezan a aportar al $500.`);
      if (d.notEligible10Count > 0) tips.push(`• Tenés ${d.notEligible10Count} proceso${d.notEligible10Count !== 1 ? 's' : ''} cerca del umbral de 10 sesiones — llevarlos ahí los sumará al universo de $1.000/$2.000.`);
      tips.push(`• Mantener activos los procesos de 10+ sesiones es clave: cada sesión de esos procesos aporta a los tres bonos al mismo tiempo.`);
      tips.push(`• La retención importa — un proceso activo 3 meses aporta más que uno nuevo que empieza a mitad de trimestre.`);
      return tips.join('\n\n');
    },
  },
  {
    triggers: ['dos bonos', 'varios bonos', 'cobrar los dos', 'cobrar los tres', 'acumular bonos', 'excluyentes', 'puedo cobrar'],
    answer: () =>
      'Los bonos son excluyentes — solo cobrás el más alto que alcances en el trimestre.\n\n' +
      'Si llegás al $2.000, ese es el que cobrás. El $500 y el $1.000 quedan "superados".\n\n' +
      'Por eso no tiene sentido detenerse en el $500 si podés ir por más — el esfuerzo extra se suma al mismo proceso.',
  },
  {
    triggers: ['inactividad', 'tasa', 'inactivo', 'paciente inactivo', 'proceso inactivo', '30 días', 'sin sesión'],
    answer: () =>
      'Un proceso pasa a inactivo cuando transcurren 30 días sin ninguna sesión. Esto afecta tu tasa de inactividad.\n\n' +
      'La tasa de inactividad no bloquea directamente el bono, pero sí afecta cuántos pacientes nuevos te asignan — y tener más procesos activos es la base para acumular sesiones elegibles.\n\n' +
      'La cadena causal: retención alta → más asignaciones → más procesos activos → más sesiones elegibles → bono más alto.',
  },
  {
    triggers: ['nueva', 'nuevo', 'período inicial', 'soy nueva', 'recién empiezo', 'recien empiezo', 'cuándo me aplica', 'cuando me aplica'],
    answer: () =>
      'El Programa Psi+ 2.0 aplica a todas las psicólogas que ya completaron su período inicial.\n\n' +
      'Durante el período inicial estás en etapa de adaptación a la clínica. Cuando lo completés, automáticamente empezás a acumular sesiones elegibles y el programa se activa para vos.',
  },
  {
    triggers: ['gracias', 'gracia', 'genial', 'perfecto', 'entendí', 'entendi', 'copado', 'ok', 'okey', '👍', '🙏'],
    answer: () =>
      '¡Con gusto! Si surge alguna otra duda sobre el programa, acá estoy. 😊',
  },
];

// ─── Intent detection ─────────────────────────────────────────────────────────

function detectAndAnswer(input: string, data: ChatbotData): string {
  const normalized = input.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  let bestScore = 0;
  let bestEntry: KnowledgeEntry | null = null;

  for (const entry of KNOWLEDGE) {
    let score = 0;
    for (const trigger of entry.triggers) {
      if (normalized.includes(trigger.normalize('NFD').replace(/[̀-ͯ]/g, ''))) {
        score += trigger.length;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestEntry = entry;
    }
  }

  if (bestEntry && bestScore > 0) {
    return bestEntry.answer(data);
  }

  return (
    'No estoy segura de entender bien la pregunta. Podés preguntarme sobre:\n\n' +
    '• Cómo funciona el programa\n' +
    '• Qué es una sesión elegible\n' +
    '• Cuánto te falta para un bono\n' +
    '• La diferencia entre los universos de 5+ y 10+\n' +
    '• Estrategias para mejorar tu progreso'
  );
}

// ─── Suggested questions ──────────────────────────────────────────────────────

const SUGGESTED: string[] = [
  '¿Cómo funciona el programa?',
  '¿Qué es una sesión elegible?',
  '¿Cuánto me falta para el próximo bono?',
  '¿Cómo mejoro mi progreso?',
  '¿En qué se diferencian los universos de 5+ y 10+?',
];

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  data: ChatbotData;
  /** Controla la visibilidad del panel — reemplaza el globo flotante que abría/cerraba el chat. */
  open: boolean;
  onClose: () => void;
}

export const PsiPlusChatbot: React.FC<Props> = ({ data, open, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nextId = useRef(1);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      if (messages.length === 0) {
        const welcome: Message = {
          id: nextId.current++,
          role: 'assistant',
          text: data.isNueva
            ? '¡Hola! Soy tu asistente del Programa Psi+ 2.0. Todavía estás en el período inicial — el programa se activa cuando lo completes. Mientras tanto puedo contarte cómo funciona.'
            : `¡Hola! Soy tu asistente del Programa Psi+ 2.0. Puedo explicarte cómo funciona el programa, qué son las sesiones elegibles, cómo va tu progreso este trimestre (${data.quarter}) y más. ¿Qué querés saber?`,
        };
        setMessages([welcome]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;

    const userMsg: Message = { id: nextId.current++, role: 'user', text: trimmed };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      const answer = detectAndAnswer(trimmed, data);
      const botMsg: Message = { id: nextId.current++, role: 'assistant', text: answer };
      setMessages(prev => [...prev, botMsg]);
      setIsTyping(false);

      // Log question + answer (fire-and-forget)
      const session = getSessionData();
      void logChatbotQuestion({
        psychologist_id: session?.psychologist_id ?? 'unknown',
        psychologist_name: session ? `${session.user.name} ${session.user.surname}`.trim() : 'Desconocida',
        question: trimmed,
        answer,
        quarter: data.quarter,
      });
    }, 600 + Math.random() * 400);
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  }

  const showSuggestions = messages.length <= 1 && !isTyping;

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed bottom-6 right-6 z-[8000]">
      {/* Chat panel */}
      <div className="rounded-2xl border border-violet-200 bg-white shadow-2xl flex flex-col overflow-hidden w-[360px]" style={{ height: 460 }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#2D2049] text-white shrink-0">
          <div className="flex items-center gap-2">
            <MessageCircle size={16} />
            <span className="text-sm font-semibold">Asistente Psi+ 2.0</span>
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3 bg-[#F8F6FF]">
          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                  msg.role === 'user'
                    ? 'bg-[#2D2049] text-white rounded-br-sm'
                    : 'bg-white border border-violet-100 text-gray-700 rounded-bl-sm shadow-sm'
                }`}
              >
                {msg.text}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-white border border-violet-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex gap-1 items-center">
                {[0, 1, 2].map(i => (
                  <span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block"
                    style={{ animation: `chatDot 1.2s ease-in-out ${i * 0.2}s infinite` }}
                  />
                ))}
              </div>
            </div>
          )}

          {showSuggestions && (
            <div className="flex flex-col gap-2 pt-1">
              <p className="text-xs text-gray-400 font-medium">Preguntas frecuentes</p>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTED.map(q => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => sendMessage(q)}
                    className="text-xs bg-white border border-violet-200 text-violet-700 px-3 py-1.5 rounded-full hover:bg-violet-50 hover:border-violet-400 transition-colors text-left"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-3 py-3 bg-white border-t border-violet-100 shrink-0 flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Preguntá sobre el programa..."
            className="flex-1 text-sm bg-[#F8F6FF] border border-violet-100 rounded-xl px-3.5 py-2 outline-none focus:ring-1 focus:ring-violet-300 placeholder-gray-400"
          />
          <button
            type="button"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || isTyping}
            className="bg-[#2D2049] hover:bg-[#4A3D73] disabled:opacity-40 text-white p-2 rounded-xl transition-colors"
          >
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
